import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';
import { ASSETS } from '@/constants/assets';

const services = [
  { title: 'Lubrificação Preventiva', desc: 'Programas de lubrificação preventiva para aumento da vida útil e redução de desgaste prematuro de componentes.' },
  { title: 'Lubrificação Corretiva', desc: 'Lubrificação específica para correção de condições anormais de desgaste e superaquecimento.' },
  { title: 'Inspeções de Lubrificação', desc: 'Inspeções técnicas para avaliar condições de lubrificação, contaminação e desgaste de componentes.' },
  { title: 'Gestão de Pontos de Lubrificação', desc: 'Cadastramento, organização e gestão de todos os pontos de lubrificação dos equipamentos.' },
  { title: 'Aplicação de Graxas e Lubrificantes', desc: 'Aplicação correta de graxas e lubrificantes conforme especificações técnicas dos fabricantes.' },
  { title: 'Apoio a Programas de Manutenção', desc: 'Integração dos serviços de lubrificação aos programas de manutenção preventiva e preditiva do cliente.' },
  { title: 'Controle de Contaminação', desc: 'Monitoramento e controle de contaminação de lubrificantes para preservação de componentes.' },
  { title: 'Boas Práticas de Lubrificação', desc: 'Treinamento e implantação de boas práticas de lubrificação industrial para aumento de confiabilidade.' },
];

const segments = [
  'Papel e Celulose',
  'Mineração',
  'Portos',
  'Siderurgia',
  'Energia',
];

export default function LubrificacaoIndustrialPage() {
  useSeo({
    title: 'Lubrificação Industrial | Serviços de Campo | Pilar',
    description: 'Serviços de lubrificação industrial em campo. Lubrificação preventiva e corretiva, gestão de pontos de lubrificação, controle de contaminação e apoio a programas de manutenção.',
    keywords: 'lubrificação industrial, manutenção de lubrificação, lubrificação preventiva, lubrificação corretiva, gestão de lubrificação, pontos de lubrificação, controle de contaminação, Grupo Pilar, Aracruz, ES',
    canonical: 'https://www.pilarmanutencao.com.br/lubrificacao-industrial',
    ogUrl: 'https://www.pilarmanutencao.com.br/lubrificacao-industrial',
    ogImage: ASSETS.oficina,
  });

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/SERVICOS-DE-CAMPO/LUBRIFICACAO-INDUSTRIAL/HERO/HERO-LUBRIFICACAO-INDUSTRIAL.webp"
              alt="Serviços de lubrificação industrial em campo - Grupo Pilar"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-[2px] bg-gp-yellow"></div>
                <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Serviços de Campo</span>
              </div>
              <h1 className="font-display text-4xl md:text-6xl font-bold text-white uppercase leading-tight mb-6">
                <span className="text-gp-yellow">Lubrificação</span> Industrial
              </h1>
              <p className="text-gray-300 text-lg md:text-xl max-w-2xl leading-relaxed">
                Serviços de lubrificação industrial voltados para aumento da vida útil dos equipamentos e redução de falhas operacionais.
              </p>
            </div>
          </div>
        </section>

        {/* PRINCIPAIS SERVIÇOS */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Principais Serviços</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="group bg-gp-graphite hover:bg-gp-graphite-lighter p-6 rounded-lg transition-all duration-300 hover:-translate-y-1 border border-transparent hover:border-gp-yellow/20"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center mb-4 group-hover:bg-gp-yellow transition-colors duration-300">
                    <span className="text-gp-yellow group-hover:text-gp-graphite font-display font-bold text-sm transition-colors duration-300">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="font-display text-base font-bold text-white uppercase mb-2 group-hover:text-gp-yellow transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{service.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEGMENTOS ATENDIDOS */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Segmentos Atendidos</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {segments.map((segment, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-lg px-5 py-4 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-2-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{segment}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Atendimento em Aracruz */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="relative rounded-xl overflow-hidden border border-gp-yellow/20">
                <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/10 to-gp-graphite"></div>
                <div className="relative z-10 p-8 md:p-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center">
                      <i className="ri-map-pin-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <h2 className="font-display text-xl font-bold text-white uppercase tracking-wider">Atendimento em Aracruz</h2>
                  </div>
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    A Pilar oferece serviços de lubrificação industrial em campo com equipe própria em Aracruz e região, atendendo as indústrias de celulose, florestal, portos e mineração.
                  </p>
                  <Link to="/lubrificacao-industrial-aracruz" className="inline-flex items-center gap-2 text-gp-yellow hover:text-white transition-colors duration-300 font-semibold text-sm uppercase tracking-wider">
                    <span>Conheça nossa atuação em Aracruz</span>
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Precisa de apoio em lubrificação industrial?
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Solicite uma avaliação técnica e conte com o Grupo Pilar para programas de lubrificação industrial e manutenção de equipamentos.
                </p>
                <a
                  href="https://wa.me/5527993092922"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                    <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span>Solicitar Apoio em Lubrificação</span>
                  <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Home */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center justify-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para a Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}